/**
 * Cross-platform analytics seam. Mirrors [triggerNudgeHaptic]'s top-level expect/actual style — no
 * DI needed. Real backends: Firebase Analytics on Android/iOS and gtag/GA4 on web. Desktop logs
 * to stdout so events stay observable while developing.
 */
expect fun logAnalyticsEvent(name: String, params: Map<String, String> = emptyMap())

/**
 * Typed funnel events for Banko. Every event name and its params live here so call sites stay
 * consistent and the event vocabulary is discoverable in one place — never pass raw strings to
 * [logAnalyticsEvent] from feature code.
 */
object Analytics {
    fun appOpen() = logAnalyticsEvent("app_open")

    fun inviteOpened(source: String) =
        logAnalyticsEvent("invite_opened", mapOf("source" to source))

    fun installInviteRecovered() =
        logAnalyticsEvent("install_invite_recovered", mapOf("platform" to "android"))

    fun lobbyCreateAttempt(offline: Boolean) =
        logAnalyticsEvent("lobby_create_attempt", mapOf("mode" to mode(offline)))

    fun lobbyCreated(offline: Boolean, usedPlusPass: Boolean) =
        logAnalyticsEvent(
            "lobby_created",
            mapOf("mode" to mode(offline), "plus_pass" to usedPlusPass.toString()),
        )

    fun lobbyCreateFailed(offline: Boolean, reason: String) =
        logAnalyticsEvent(
            "lobby_create_failed",
            mapOf("mode" to mode(offline), "reason" to reason),
        )

    fun lobbyViewed(role: String, humanCount: Int, botCount: Int, offline: Boolean) =
        logAnalyticsEvent(
            "lobby_viewed",
            mapOf(
                "role" to role,
                "human_count" to humanCount.toString(),
                "bot_count" to botCount.toString(),
                "mode" to mode(offline),
            ),
        )

    fun lobbyGuestJoined(guestCount: Int, humanCount: Int) =
        logAnalyticsEvent(
            "lobby_guest_joined",
            mapOf(
                "guest_count" to guestCount.toString(),
                "human_count" to humanCount.toString(),
            ),
        )

    fun lobbyAbandoned(humanCount: Int, botCount: Int, offline: Boolean) =
        logAnalyticsEvent(
            "lobby_abandoned",
            mapOf(
                "human_count" to humanCount.toString(),
                "bot_count" to botCount.toString(),
                "mode" to mode(offline),
            ),
        )

    fun hostedGame(offline: Boolean) =
        logAnalyticsEvent("hosted_game", mapOf("mode" to mode(offline)))

    fun joinedGame(offline: Boolean, via: JoinSource) =
        logAnalyticsEvent("joined_game", mapOf("mode" to mode(offline), "via" to via.param))

    /** How a player reached a game: invite deep link, hand-typed code, or the public games browser. */
    enum class JoinSource(val param: String) {
        InviteLink("invite_link"),
        Manual("manual"),
        PublicGames("public_games"),
    }

    /** Legacy native share entry point; the Compose lobby supplies roster counts below. */
    fun inviteShared() = logAnalyticsEvent("invite_shared")

    fun inviteShared(humanCount: Int, botCount: Int) =
        logAnalyticsEvent(
            "invite_shared",
            mapOf(
                "human_count" to humanCount.toString(),
                "bot_count" to botCount.toString(),
            ),
        )

    fun gameRecapShared() = logAnalyticsEvent("game_recap_shared")

    /** The one-time first-launch practice offer: the new player either took it or dismissed it. */
    fun practiceOffer(accepted: Boolean) =
        logAnalyticsEvent(
            "practice_offer",
            mapOf("action" to if (accepted) "played" else "dismissed"),
        )

    fun joinAttempted(via: JoinSource) =
        logAnalyticsEvent("join_attempted", mapOf("via" to via.param))

    fun joinFailed(via: JoinSource, reason: String) =
        logAnalyticsEvent(
            "join_failed",
            mapOf("via" to via.param, "reason" to reason),
        )

    fun publicGamesOpened() = logAnalyticsEvent("public_games_opened")

    fun publicGamesResult(gameCount: Int) =
        logAnalyticsEvent(
            "public_games_result",
            mapOf("game_count_bucket" to countBucket(gameCount)),
        )

    /** Rewarded-ad Plus Pass: earned by finishing the ad; used when a created game consumes it. */
    fun plusPassEarned() = logAnalyticsEvent("plus_pass_earned")

    fun plusPassUsed() = logAnalyticsEvent("plus_pass_used")

    /** Rewarded-ad one-game dice trial: earned from a locked cosmetic's store view; used at create/join. */
    fun diceTrialEarned(cosmeticId: String) =
        logAnalyticsEvent("dice_trial_earned", mapOf("cosmetic" to cosmeticId))

    fun diceTrialUsed(cosmeticId: String) =
        logAnalyticsEvent("dice_trial_used", mapOf("cosmetic" to cosmeticId))

    fun gameStarted(playerCount: Int, botCount: Int, ginko: Boolean, offline: Boolean) {
        logAnalyticsEvent(
            "game_started",
            mapOf(
                "player_count" to playerCount.toString(),
                "human_count" to (playerCount - botCount).coerceAtLeast(0).toString(),
                "bot_count" to botCount.toString(),
                "ginko" to ginko.toString(),
                "mode" to mode(offline),
            ),
        )
        // Surface bot games as their own event too, so "how many games include bots" is a single
        // metric rather than a filter on game_started.
        if (botCount > 0) {
            logAnalyticsEvent("bot_game_started", mapOf("bot_count" to botCount.toString()))
        }
    }

    /**
     * A game reached its end. Beyond the table size, this carries the shape of the game — how
     * swingy it was, how often the table busted — so balance changes (Ginko odds, bot nerve) can be
     * judged against real play rather than guesses.
     */
    fun gameFinished(game: models.DomainGame, offline: Boolean) {
        val analytics = models.GameAnalyticsCalculator.from(game)
        val standings = analytics.standings
        val winningScore = standings.firstOrNull()?.points ?: 0
        val runnerUpScore = standings.getOrNull(1)?.points ?: 0
        logAnalyticsEvent(
            "game_finished",
            mapOf(
                "player_count" to game.players.size.toString(),
                "human_count" to game.players.count { !it.isBot }.toString(),
                "bot_count" to game.players.count { it.isBot }.toString(),
                "round_count" to game.endRoundNum.toString(),
                "mode" to mode(offline),
                "ginko" to models.GameSettings.isEnabled(game.settings, models.GameSettings.GinkoMode).toString(),
                "winning_score" to winningScore.toString(),
                "win_margin" to (winningScore - runnerUpScore).toString(),
                "total_rolls" to analytics.totals.rolls.toString(),
                "busts" to analytics.totals.busts.toString(),
                "banks" to analytics.totals.banks.toString(),
                "avg_peak_pot" to (analytics.totals.averagePeakPot ?: 0).toString(),
                "stats_complete" to analytics.historyComplete.toString(),
            ),
        )
    }

    /** This device folded a finished game into its lifetime stats. */
    fun careerGameRecorded(won: Boolean, gamesPlayed: Int, winStreak: Int) =
        logAnalyticsEvent(
            "career_game_recorded",
            mapOf(
                "result" to if (won) "win" else "loss",
                "games_played" to gamesPlayed.toString(),
                "win_streak" to winStreak.toString(),
            ),
        )

    /** Where a stats view was opened from — tells us which entry points players actually use. */
    enum class StatsSource(val param: String) {
        Home("home"),
        InGame("in_game"),
        GameOver("game_over"),
    }

    fun statsViewed(source: StatsSource) =
        logAnalyticsEvent("stats_viewed", mapOf("source" to source.param))

    fun careerStatsReset(gamesPlayed: Int) =
        logAnalyticsEvent("career_stats_reset", mapOf("games_played" to gamesPlayed.toString()))

    fun firstRoll(role: String, offline: Boolean, manual: Boolean) =
        logAnalyticsEvent(
            "first_roll",
            mapOf(
                "role" to role,
                "mode" to mode(offline),
                "manual" to manual.toString(),
            ),
        )

    fun firstBank(role: String, offline: Boolean, points: Int) =
        logAnalyticsEvent(
            "first_bank",
            mapOf(
                "role" to role,
                "mode" to mode(offline),
                "pot_bucket" to pointsBucket(points),
            ),
        )

    fun rematchStarted(humanCount: Int, botCount: Int) =
        logAnalyticsEvent(
            "rematch_started",
            mapOf(
                "human_count" to humanCount.toString(),
                "bot_count" to botCount.toString(),
            ),
        )

    fun ginkoEnabled() = logAnalyticsEvent("ginko_enabled")

    fun tableReactionSent(kind: models.TableReactionKind) =
        logAnalyticsEvent(
            "table_reaction_sent",
            mapOf("reaction" to kind.name.lowercase()),
        )

    private fun mode(offline: Boolean) = if (offline) "offline" else "online"

    private fun countBucket(count: Int): String = when {
        count <= 0 -> "0"
        count == 1 -> "1"
        count <= 3 -> "2_3"
        else -> "4_plus"
    }

    private fun pointsBucket(points: Int): String = when {
        points < 50 -> "under_50"
        points < 100 -> "50_99"
        points < 250 -> "100_249"
        else -> "250_plus"
    }
}
